import React from "react";
import { Link } from "react-router-dom";
import PolicyPage, { POLICY_ADDRESS, PolicyEmail } from "./PolicyPage";

const policyDirectory = [
  ["/privacy", "Privacy Policy", "Information collection, use, disclosure, retention, and rights."],
  ["/privacy-choices", "Your Privacy Choices", "Access, deletion, correction, opt-out, and appeal requests."],
  ["/cookies", "Cookie & Storage Policy", "Cart storage, necessary technologies, providers, and browser controls."],
  ["/terms", "Terms of Service", "Rules governing the storefront, purchases, and site use."],
  ["/acceptable-use", "Acceptable Use Policy", "Prohibited misuse, automated access, fraud, and interference."],
  ["/shipping", "Shipping & Delivery Policy", "Processing, tracking, estimates, customs, damage, and loss."],
  ["/returns", "Return & Refund Policy", "Return eligibility, exceptions, shipping, inspection, and refunds."],
  ["/orders", "Order & Cancellation Policy", "Order acceptance, changes, cancellation, and availability."],
  ["/payments", "Payment & Billing Policy", "Currency, authorization, payment security, refunds, and disputes."],
  ["/pricing", "Pricing, Tax & Customs Policy", "Pricing errors, taxes, duties, exchange rates, and totals."],
  ["/promotions", "Promotions Policy", "Offer eligibility, limits, returns, and technical errors."],
  ["/product-safety", "Product Information & Safety", "Product variation, intended use, warnings, and recalls."],
  ["/warranty", "Warranty & Complaints Policy", "Defects, legal guarantees, remedies, and escalation."],
  ["/reviews-policy", "Reviews & Testimonials Policy", "Moderation, incentives, authenticity, and disclosure."],
  ["/intellectual-property", "Intellectual Property & Rights", "Ownership, permitted use, and rights complaints."],
  ["/accessibility", "Accessibility Statement", "Our accessibility approach and feedback channel."],
  ["/security", "Security & Vulnerability Reporting", "Safe reporting rules and what to include in a report."],
  ["/communications", "Electronic Communications Policy", "Transactional messages, marketing, and opt-outs."],
  ["/legal-notice", "Legal Notice", "Store identity, official contact, policy status, and notices."],
];

export function PolicyCenter() {
  return (
    <PolicyPage
      title="Policy center"
      intro="This directory brings together the customer-service, commerce, privacy, safety, and legal policies for the slayfulpawsco storefront."
      showDirectoryLink={false}
    >
      <p>
        These policies are written specifically for this storefront and should
        be read together. If a policy conflicts with a mandatory right in your
        location, the mandatory rule controls. Questions may be sent to
        <PolicyEmail subject="Store policy question" />.
      </p>
      <div className="policy-grid">
        {policyDirectory.map(([to, title, description]) => (
          <Link className="policy-card" to={to} key={to}>
            <strong>{title}</strong>
            <span>{description}</span>
          </Link>
        ))}
      </div>
      <p className="policy-note">
        Policy pages explain how the store is intended to operate; they are not
        a substitute for legal advice. Additional product-specific or
        promotion-specific terms shown before purchase also apply to the
        relevant order.
      </p>
    </PolicyPage>
  );
}

export function OrdersPolicy() {
  return (
    <PolicyPage
      title="Order and cancellation policy"
      intro="This policy explains how an order is submitted, reviewed, accepted, changed, or cancelled."
    >
      <h2>Before you place an order</h2>
      <p>
        Review the product, options, quantity, price, contact details, and
        delivery information before paying. You are responsible for providing
        information that is current and complete. Contact us promptly if you
        notice an error.
      </p>

      <h2>Order submission and acceptance</h2>
      <p>
        Placing an order is an offer to buy. A payment or order receipt confirms
        that the request was received, not necessarily that it was accepted.
        Acceptance occurs when we expressly confirm acceptance or dispatch the
        goods, unless local law provides otherwise.
      </p>

      <h2>Verification and quantity limits</h2>
      <p>
        We may reasonably verify payment, contact, address, or fraud indicators
        before acceptance. We may limit quantities intended for resale or split
        unusually large orders. We will not request a password or complete card
        credentials by email.
      </p>

      <h2>Customer-requested changes or cancellation</h2>
      <p>
        Email <PolicyEmail subject="Order change or cancellation" /> as soon as
        possible with the order number. We will try to make the requested change
        before packing or dispatch, but cannot guarantee it. If dispatch has
        already occurred, the <Link to="/returns">Return and Refund Policy</Link>
        applies. Statutory cancellation rights remain available where required.
      </p>

      <h2>Cancellation by slayfulpawsco</h2>
      <p>
        We may reject or cancel an order for stock unavailability, an obvious
        price or description error, an unsupported destination, payment failure,
        suspected fraud, sanctions or legal restrictions, or misuse of an offer.
        If payment was captured for a cancelled item, an appropriate refund
        will be issued to the original payment method.
      </p>

      <h2>Unavailable items and delays</h2>
      <p>
        If an item becomes unavailable, we will not substitute a materially
        different product without permission. If dispatch is delayed beyond a
        promised or legally required time, we will provide the notice, choice,
        cancellation, and refund required by applicable law.
      </p>

      <h2>Order records</h2>
      <p>
        Keep your confirmation and order number. We may retain transaction
        records for support, fraud prevention, tax, accounting, and legal
        purposes as described in the <Link to="/privacy">Privacy Policy</Link>.
      </p>
    </PolicyPage>
  );
}

export function PaymentsPolicy() {
  return (
    <PolicyPage
      title="Payment and billing policy"
      intro="This policy describes payment authorization, billing accuracy, payment security, refunds, and payment disputes."
    >
      <h2>Currency and payment provider</h2>
      <p>
        The current checkout submits charges in U.S. dollars (USD). Payment is
        completed on a third-party hosted checkout. The available payment
        methods are those shown by that provider at the time of payment and may
        vary by device, country, and transaction.
      </p>

      <h2>Authorization and capture</h2>
      <p>
        By submitting payment, you confirm that you are authorized to use the
        selected method and authorize the amount shown. A bank may place a
        temporary authorization hold. A failed or cancelled attempt can still
        leave a temporary hold that the bank later releases.
      </p>

      <h2>Billing information</h2>
      <p>
        Provide accurate billing and contact information. The payment provider
        or issuer may decline a transaction or request authentication. We are
        not responsible for issuer fees, overdraft charges, foreign transaction
        fees, or exchange-rate adjustments that were not charged by us.
      </p>

      <h2>Payment security</h2>
      <p>
        Full card credentials are intended to be entered only on the payment
        provider’s secure checkout, not sent to slayfulpawsco by email or the
        contact form. We do not intentionally store full card numbers or card
        security codes. See the <Link to="/privacy">Privacy Policy</Link> and
        <Link to="/security"> Security Policy</Link> for more information.
      </p>

      <h2>Payment failure or duplicate charge</h2>
      <p>
        If checkout reports a failure, confirm your details or contact the card
        issuer before retrying. If you believe you were charged twice, email us
        with the order number, dates, amounts, and the last four digits of the
        payment method. Do not send the full card number.
      </p>

      <h2>Refunds</h2>
      <p>
        Approved refunds are normally issued to the original payment method.
        The payment provider and issuer control how quickly the credit appears.
        If the original method cannot receive a refund, we will arrange a lawful
        alternative after appropriate verification.
      </p>

      <h2>Chargebacks and payment disputes</h2>
      <p>
        Contact us first if possible so we can investigate. Nothing prevents
        you from using a valid legal or card-network dispute right. We may give
        the payment provider relevant order, delivery, communication, and refund
        records to respond to a dispute.
      </p>
    </PolicyPage>
  );
}

export function PricingPolicy() {
  return (
    <PolicyPage
      title="Pricing, tax and customs policy"
      intro="This policy explains displayed prices, errors, taxes, currency conversion, duties, and the final order total."
    >
      <h2>Displayed prices</h2>
      <p>
        Product prices are displayed in U.S. dollars unless the page clearly
        states another currency. The price and total presented for confirmation
        at checkout control, subject to correction of an obvious error before
        order acceptance and any mandatory consumer law.
      </p>

      <h2>Errors and changes</h2>
      <p>
        Prices and availability may change before an order is accepted. If an
        accepted order contains a material pricing or calculation error, we
        will contact you before fulfillment and offer the choice required by
        law, which may include confirming the corrected price or cancelling for
        a full refund.
      </p>

      <h2>Sales tax and similar charges</h2>
      <p>
        Applicable sales tax, value-added tax, goods and services tax, or a
        similar charge may be included in a displayed price or added to the
        order total depending on the destination and how the sale is structured.
        Review the total before payment.
      </p>

      <h2>Customs duties and import fees</h2>
      <p>
        Unless checkout expressly says duties and import charges are included,
        the recipient is responsible for destination-country duties, taxes,
        brokerage, and customs fees. These amounts are set by authorities or
        carriers and may not be known to us in advance.
      </p>

      <h2>Currency conversion</h2>
      <p>
        A bank or payment provider may convert USD to your account currency and
        may set its own exchange rate or fee. Refund conversion can differ from
        the original conversion because rates change. Those differences are not
        an additional charge by slayfulpawsco.
      </p>

      <h2>Shipping and promotions</h2>
      <p>
        Shipping charges and valid discounts are reflected in the confirmed
        total when applicable. Promotion conditions are explained in the
        <Link to="/promotions"> Promotions Policy</Link> or the specific offer.
      </p>
    </PolicyPage>
  );
}

export function PromotionsPolicy() {
  return (
    <PolicyPage
      title="Promotions and discount policy"
      intro="This policy applies when slayfulpawsco offers a discount, coupon, bundle price, giveaway, or other promotion. Specific offer terms control if they differ."
    >
      <h2>Eligibility and duration</h2>
      <p>
        An offer may be limited by date, destination, customer, product,
        quantity, minimum spend, or available stock. Time zones and expiration
        times are those stated in the offer. A promotion has no cash value and
        is not transferable unless expressly stated.
      </p>

      <h2>Redemption</h2>
      <p>
        Apply any required code before submitting payment. Unless the offer says
        otherwise, one code may be used per order, offers cannot be combined,
        and a discount cannot be added retroactively after an order is placed.
        Taxes, shipping, and excluded products may not count toward a threshold.
      </p>

      <h2>Returns and refunds</h2>
      <p>
        A refund for a returned promotional item is based on the amount actually
        paid for that item, subject to applicable law. If returning part of a
        bundle causes the remaining items to lose a stated bundle discount, a
        lawful adjustment may be made if the original offer clearly disclosed
        that condition.
      </p>

      <h2>Misuse and technical errors</h2>
      <p>
        We may refuse duplicated, altered, resold, automated, or fraudulently
        obtained codes and may correct a genuine technical or publication
        error. We will not use this section to remove a completed customer right
        that cannot legally be withdrawn.
      </p>

      <h2>Giveaways</h2>
      <p>
        A giveaway or prize promotion will have separate official rules covering
        eligibility, entry, timing, selection, prize, and required disclosures.
        No giveaway is offered merely because this general policy exists.
      </p>
    </PolicyPage>
  );
}

export function ProductSafetyPolicy() {
  return (
    <PolicyPage
      title="Product information and safety policy"
      intro="This policy explains product descriptions, ordinary variation, care, safe use, and how to report a product-safety concern."
    >
      <h2>Descriptions and variation</h2>
      <p>
        We aim to describe dimensions, materials, color, compatibility, and
        included parts accurately. Measurements may be rounded, screens may
        display color differently, and handmade or natural materials may vary
        slightly. The product page and packaging should be read together.
      </p>

      <h2>Intended use</h2>
      <p>
        Stationery and desk accessories should be used only for their stated
        purpose. Follow warnings and care instructions supplied with the item.
        Do not use writing inks, coatings, adhesives, or craft materials on skin,
        food, or other unintended surfaces unless the product expressly states
        that use is safe.
      </p>

      <h2>Children and small parts</h2>
      <p>
        Some products may have small parts, sharp points, magnets, glass, metal,
        or breakable components and may not be toys. Keep unsuitable items away
        from young children and supervise use according to the product warning
        and the user’s abilities.
      </p>

      <h2>Glass, blades, points, and breakage</h2>
      <p>
        Handle glass pens, scissors, cutters, nibs, and pointed instruments with
        care. Inspect breakable items before use and stop using an item that is
        cracked, chipped, loose, or otherwise unsafe. Dispose of broken
        materials safely under local rules.
      </p>

      <h2>Materials and sensitivities</h2>
      <p>
        If you have an allergy or sensitivity, review disclosed materials and
        ask before purchase when information is unclear. Stop use if irritation
        occurs and seek appropriate professional advice for a serious reaction.
      </p>

      <h2>Safety concerns and recalls</h2>
      <p>
        Report an incident, suspected hazard, missing warning, or recall concern
        to <PolicyEmail subject="Product safety report" /> with the order number,
        product, photos, and a factual description. Stop using the item while a
        serious concern is reviewed. If a recall or corrective action applies,
        we will provide available instructions to affected customers as
        required by law.
      </p>
    </PolicyPage>
  );
}

export function WarrantyPolicy() {
  return (
    <PolicyPage
      title="Warranty and complaints policy"
      intro="This policy explains how to raise a quality complaint, what information helps us review it, and how remedies are determined."
    >
      <h2>Mandatory legal guarantees</h2>
      <p>
        Products may carry non-waivable legal guarantees concerning quality,
        conformity, description, durability, or fitness under the law that
        applies to the sale. Nothing in this policy limits those rights.
      </p>

      <h2>Additional written warranties</h2>
      <p>
        No separate voluntary or manufacturer warranty should be assumed unless
        it is stated on the product page or supplied with the product. Any such
        warranty is governed by its written duration, coverage, exclusions, and
        claim instructions, in addition to mandatory legal rights.
      </p>

      <h2>Making a quality complaint</h2>
      <p>
        Email <PolicyEmail subject="Product quality complaint" /> with your order
        number, product name, when the issue appeared, how the product was used,
        and clear photos or video where practical. Do not send the item until we
        provide instructions.
      </p>

      <h2>Assessment and remedies</h2>
      <p>
        We may request reasonable evidence or inspection to identify the issue.
        Depending on the item, facts, stock, and applicable law, a remedy may be
        repair, replacement, price reduction, or refund. We will cover
        reasonable return costs when required by law or when we confirm that the
        item was defective when supplied.
      </p>

      <h2>What is not normally a defect</h2>
      <p>
        Normal wear, expected consumable depletion, accidental damage, improper
        storage, misuse, unauthorized modification, or a disclosed handmade or
        material variation is not normally a manufacturing defect. This does
        not affect a remedy where the product was unsafe, misdescribed, or not
        of the quality required by law.
      </p>

      <h2>Complaint escalation</h2>
      <p>
        If you disagree with the first response, reply with the subject “Review
        requested,” explain the unresolved point, and include any new evidence.
        We will conduct a second review. You may also use any regulator, court,
        alternative-dispute, or payment remedy available under applicable law.
      </p>
    </PolicyPage>
  );
}

export function PrivacyChoices() {
  return (
    <PolicyPage
      title="Your privacy choices"
      intro="Use this page to understand and exercise privacy choices that may apply to you. Availability depends on your location and the nature of the information."
    >
      <h2>Available requests</h2>
      <p>You may ask us to:</p>
      <ul>
        <li>confirm whether we process your personal information and provide access to it;</li>
        <li>correct inaccurate information;</li>
        <li>delete information, subject to lawful exceptions;</li>
        <li>provide certain information in a portable format;</li>
        <li>restrict or object to certain processing;</li>
        <li>withdraw consent for future processing based on consent; or</li>
        <li>opt out of a sale, sharing, or targeted advertising if such activity applies.</li>
      </ul>

      <h2>Current sale and sharing status</h2>
      <p>
        Based on the current storefront configuration, slayfulpawsco does not
        sell personal information for money and does not share it for
        cross-context behavioral advertising. We do not knowingly sell or share
        personal information of children for those purposes.
      </p>

      <h2>How to submit a request</h2>
      <p>
        Email <PolicyEmail subject="Privacy rights request" /> with “Privacy
        Request” in the subject and describe the right you want to exercise.
        Include the email used for your order and an order number if relevant,
        but do not email payment credentials or government identification unless
        we later explain that it is necessary through an appropriate channel.
      </p>

      <h2>Verification and authorized agents</h2>
      <p>
        We may ask for information reasonably needed to match the request to our
        records and protect you from unauthorized disclosure. An authorized
        agent may submit a request where local law allows; we may request proof
        of authority and direct confirmation from the individual unless an
        exception applies.
      </p>

      <h2>Timing, exceptions, and appeals</h2>
      <p>
        We will acknowledge and answer within the time required by applicable
        law. Some records may be retained to complete a transaction, protect
        security, maintain tax or legal records, or establish or defend legal
        claims. If we deny a request, we will explain the reason where required.
        To appeal, reply with “Privacy appeal” and explain why you disagree.
      </p>

      <h2>Browser preference signals</h2>
      <p>
        Where legally required and technically supported, we treat a recognized
        opt-out preference signal, such as Global Privacy Control, as a request
        for the browser or device that sends it. The current storefront does not
        intentionally configure cross-site advertising trackers.
      </p>

      <h2>No discrimination</h2>
      <p>
        We will not unlawfully discriminate against you for exercising an
        applicable privacy right. A choice may affect a feature only when the
        relevant information is reasonably necessary to provide that feature.
      </p>
    </PolicyPage>
  );
}

export function IntellectualPropertyPolicy() {
  return (
    <PolicyPage
      title="Intellectual property and rights policy"
      intro="slayfulpawsco respects copyrights, trademarks, designs, publicity rights, and other intellectual-property rights. This page explains permitted use and how to report a concern."
    >
      <h2>Store materials</h2>
      <p>
        Unless otherwise identified, the slayfulpawsco name, original site copy,
        original graphics, product photography created for the store, layout,
        and related original materials are owned by or licensed to
        slayfulpawsco. The site grants you a limited, revocable, non-exclusive
        right to view those materials for personal shopping and ordinary
        reference.
      </p>

      <h2>Third-party rights</h2>
      <p>
        Third-party trademarks, names, product designs, and other rights remain
        the property of their respective owners. A factual reference does not
        claim ownership, sponsorship, endorsement, or affiliation. We do not
        authorize counterfeit goods or content that unlawfully copies another
        person’s protected work.
      </p>

      <h2>Prohibited reuse</h2>
      <p>
        Unless permission or law allows it, do not reproduce, republish, sell,
        scrape for redistribution, remove rights notices from, or create a
        commercial derivative of protected site materials. Linking to a public
        page without implying endorsement is generally permitted.
      </p>

      <h2>Report copyright, trademark, or other rights concerns</h2>
      <p>
        Send a good-faith report to
        <PolicyEmail subject="Intellectual property report" /> containing:
      </p>
      <ul>
        <li>your name, contact details, and relationship to the rights owner;</li>
        <li>identification of the work, mark, design, or other right;</li>
        <li>the exact site URL and a clear description of the challenged material;</li>
        <li>why you believe the use is unauthorized, including relevant registration details if available;</li>
        <li>a statement that the report is accurate and made in good faith; and</li>
        <li>your physical or electronic signature.</li>
      </ul>

      <h2>Review process and responses</h2>
      <p>
        We may request clarification, preserve relevant records, restrict
        material while reviewing, forward the report to the affected party, or
        take other proportionate action. A person responding to a report should
        identify the material, explain the lawful basis for use, provide contact
        details, and make accurate statements. False or abusive reports may
        create legal consequences.
      </p>

      <h2>No transfer by feedback</h2>
      <p>
        Sending general feedback does not transfer your pre-existing
        intellectual-property rights. You grant us only the permission
        reasonably needed to read, evaluate, and respond to what you submit.
      </p>
    </PolicyPage>
  );
}

export function AccessibilityStatement() {
  return (
    <PolicyPage
      title="Accessibility statement"
      intro="slayfulpawsco aims to make its storefront usable by people with diverse abilities, devices, and assistive technologies."
    >
      <h2>Our approach</h2>
      <p>
        We consider semantic structure, keyboard access, readable contrast,
        text alternatives, form labels, responsive layouts, clear language, and
        reduced barriers in core shopping paths. Accessibility is an ongoing
        process, and a technical issue or third-party service may still create a
        barrier.
      </p>

      <h2>Third-party content</h2>
      <p>
        Some functionality, including hosted payment and externally hosted
        resources, is controlled by a third party. We seek workable services but
        cannot directly change every part of an independent provider’s
        interface.
      </p>

      <h2>Request assistance or an alternative</h2>
      <p>
        If you cannot access product information, use checkout, or read a policy,
        email <PolicyEmail subject="Accessibility assistance" />. Describe the
        page, task, assistive technology or browser if you are comfortable doing
        so, and the format or assistance you need. We will try to provide the
        same information or service through a reasonable alternative.
      </p>

      <h2>Feedback</h2>
      <p>
        We welcome specific accessibility feedback. Please include the page URL
        and what happened, but do not include passwords, full card information,
        or unrelated sensitive data.
      </p>
    </PolicyPage>
  );
}

export function SecurityPolicy() {
  return (
    <PolicyPage
      title="Security and vulnerability reporting"
      intro="This policy provides a safe channel for reporting a suspected security issue affecting the slayfulpawsco storefront."
    >
      <h2>How to report</h2>
      <p>
        Email <PolicyEmail subject="Security vulnerability report" /> with the
        affected URL or component, steps to reproduce, observed impact, and any
        minimal supporting evidence. Use the subject “Security report.” Do not
        include unnecessary personal information or active malicious code.
      </p>

      <h2>Responsible testing</h2>
      <p>When investigating a possible issue, do not:</p>
      <ul>
        <li>access, change, retain, or disclose data that is not yours;</li>
        <li>conduct denial-of-service, spam, social engineering, or physical attacks;</li>
        <li>degrade service, create fraudulent orders, or incur charges;</li>
        <li>use automated scanning at a volume that affects customers; or</li>
        <li>demand payment or threaten disclosure as a condition of reporting.</li>
      </ul>

      <h2>Our response</h2>
      <p>
        We will assess good-faith reports, may request clarification, and will
        prioritize work based on reproducibility and risk. We cannot promise a
        reward, public credit, a specific fix date, or disclosure of confidential
        investigation details.
      </p>

      <h2>Customer security</h2>
      <p>
        Use a current browser, verify the site address before paying, and enter
        payment credentials only on the hosted checkout. slayfulpawsco will not
        ask for your password or complete card number by email. Report suspicious
        messages through the same address.
      </p>
    </PolicyPage>
  );
}

export function CommunicationsPolicy() {
  return (
    <PolicyPage
      title="Electronic communications policy"
      intro="This policy distinguishes service messages needed for an order or request from optional marketing communications."
    >
      <h2>Transactional and service messages</h2>
      <p>
        When you place an order or ask for support, we may send messages needed
        to confirm payment, provide order or delivery updates, answer the
        request, give safety information, or administer a return or refund.
        These are service communications rather than promotional messages.
      </p>

      <h2>Optional marketing</h2>
      <p>
        We send promotional email only when there is a lawful basis and any
        consent required in your location. The current storefront does not
        require you to join a marketing list to purchase. Marketing permission
        is not a condition of receiving customer service.
      </p>

      <h2>Unsubscribe and preferences</h2>
      <p>
        Use the unsubscribe link in a marketing email or contact
        <PolicyEmail subject="Marketing opt-out" />. We may keep a minimal
        suppression record so the opt-out is respected. Opting out of marketing
        does not stop essential order, security, recall, or legal messages.
      </p>

      <h2>Delivery and security</h2>
      <p>
        Email delivery is not guaranteed and messages can be delayed or filtered.
        We will not ask you to send a password, full card number, or card
        security code by email. If a message claiming to be from slayfulpawsco
        seems suspicious, do not use its links; contact us through the address
        displayed on this site.
      </p>
    </PolicyPage>
  );
}

export function ReviewsPolicy() {
  return (
    <PolicyPage
      title="Reviews and testimonials policy"
      intro="This policy explains how product reviews and testimonials should be collected, displayed, moderated, and disclosed."
    >
      <h2>Honest experiences</h2>
      <p>
        A review should reflect the reviewer’s genuine experience and opinion.
        Reviews must not be fabricated, copied from another source, submitted
        by someone who has not used the relevant product, or used to impersonate
        another person.
      </p>

      <h2>Incentives and relationships</h2>
      <p>
        If a reviewer received a free product, discount, payment, employment,
        family relationship, or another material benefit, that connection
        should be clearly disclosed near the review. An incentive may not be
        conditioned on a positive rating or favorable wording.
      </p>

      <h2>Moderation</h2>
      <p>
        We may decline or remove content that is unrelated, unlawful,
        threatening, discriminatory, deceptive, infringing, contains personal
        or payment information, includes malicious links, or is obvious spam.
        We may correct formatting or minor spelling without changing meaning.
        We do not promise to publish every submission and should not suppress a
        genuine review merely because it is negative.
      </p>

      <h2>Permission to display</h2>
      <p>
        If you voluntarily submit a review for publication, you grant
        slayfulpawsco a non-exclusive, worldwide, royalty-free permission to
        host, reproduce, format, and display it for storefront and promotional
        purposes, subject to applicable privacy and consumer law. You retain
        ownership of your original content.
      </p>

      <h2>Questions or challenges</h2>
      <p>
        To correct attribution, disclose a relationship, report copied content,
        or raise an authenticity concern, email
        <PolicyEmail subject="Review or testimonial concern" /> with the page,
        review, and factual basis for the request.
      </p>
    </PolicyPage>
  );
}

export function AcceptableUsePolicy() {
  return (
    <PolicyPage
      title="Acceptable use policy"
      intro="This policy protects customers, rights holders, and the storefront from fraud, abuse, interference, and unlawful use."
    >
      <h2>Lawful personal use</h2>
      <p>
        You may browse, communicate with support, and purchase products for
        lawful purposes. You must provide accurate information and have
        authority to use any contact, delivery, and payment details submitted.
      </p>

      <h2>Prohibited conduct</h2>
      <p>You must not use the site to:</p>
      <ul>
        <li>commit fraud, evade a lawful restriction, or use stolen payment or identity information;</li>
        <li>upload or transmit malware, harmful code, or unlawful content;</li>
        <li>probe, bypass, or interfere with authentication, rate limits, security, availability, or another user’s session;</li>
        <li>harass, threaten, impersonate, or unlawfully collect information about another person;</li>
        <li>infringe copyright, trademark, privacy, publicity, or other rights;</li>
        <li>send spam or use automated tools to create false orders, reviews, or support requests; or</li>
        <li>scrape or reproduce protected content for resale or a competing commercial dataset without permission.</li>
      </ul>

      <h2>Reasonable automated access</h2>
      <p>
        Ordinary search-engine indexing and accessibility tools are not
        prohibited. Automated access must respect published technical controls,
        avoid personal data, and not burden the service or circumvent a measure
        intended to protect customers or content.
      </p>

      <h2>Enforcement</h2>
      <p>
        We may investigate, limit, block, preserve evidence of, or report
        suspected misuse and may cancel related orders where lawful. Measures
        should be proportionate to the risk and do not remove appeal, refund, or
        consumer rights that apply by law.
      </p>

      <h2>Report abuse</h2>
      <p>
        Send a factual report, relevant URL, and supporting detail to
        <PolicyEmail subject="Acceptable use report" />. For technical security
        issues, follow the <Link to="/security">Security Policy</Link>.
      </p>
    </PolicyPage>
  );
}

export function LegalNotice() {
  return (
    <PolicyPage
      title="Legal notice"
      intro="This notice identifies the storefront using the information supplied for publication and explains how formal questions can be directed to it."
    >
      <h2>Store identity</h2>
      <table>
        <tbody>
          <tr>
            <td>Storefront and brand name</td>
            <td>slayfulpawsco</td>
          </tr>
          <tr>
            <td>Official contact email</td>
            <td><PolicyEmail /></td>
          </tr>
          <tr>
            <td>Business mailing address</td>
            <td>{POLICY_ADDRESS}</td>
          </tr>
          <tr>
            <td>Business activity</td>
            <td>Online retail of stationery and related desk accessories</td>
          </tr>
        </tbody>
      </table>
      <p className="policy-note">
        No separate legal-entity name, registration number, tax number, or
        telephone number has been represented on this page because that
        information has not been supplied for publication. Where applicable law
        requires additional merchant details, they should be added here before
        offering sales in that jurisdiction.
      </p>

      <h2>Formal notices</h2>
      <p>
        Send a formal notice to <PolicyEmail subject="Formal legal notice" /> and
        include your name, contact information, the matter, relevant order or
        URL, requested action, and any applicable deadline. Email does not waive
        any legally required method of service.
      </p>

      <h2>Policy status</h2>
      <p>
        Policies are written for the slayfulpawsco storefront and should be read
        with product-specific and order-specific information. They do not claim
        ownership of another business’s wording, trademarks, products, or
        materials. Mandatory law prevails where it gives a customer rights that
        cannot be limited by contract.
      </p>

      <h2>Disputes and complaints</h2>
      <p>
        Please contact us first with enough detail to investigate. If a complaint
        cannot be resolved directly, either party may use a regulator, court,
        payment process, or alternative-dispute channel available under
        applicable law. No policy requires a consumer to give up a forum or
        remedy that cannot legally be waived.
      </p>
    </PolicyPage>
  );
}
